<template>
  <main class="main p42">

    <Breadcrumbs :breadcrumbs="breadcrumbs" />

    <!-- Блок 1: Hero -->
    <section class="p42-hero">
      <div class="container p42-hero__container">
        <div class="p42-hero__content">
          <h1 class="p42-hero__title title">
            Архитектура —<br>от концепции до сдачи объекта
          </h1>
          <p class="p42-hero__desc">
            Проектирование с нуля, визуализация, авторский надзор и сдача под ключ.
            Работаем с жилыми домами, отелями, офисами, торговыми пространствами и ресторанами.
            Проверенная команда архитекторов — через платформу Sharun.
          </p>
          <button class="btn main-btn" @click="isModalOpen = true">Обсудить проект</button>
          <p class="p42-hero__geo">Проектируем по всей России</p>
        </div>
        <div class="p42-hero__image">
          <img
            src="/img/services/hero-arch.jpg"
            alt="Архитектура — от концепции до сдачи объекта"
            loading="eager"
          />
        </div>
      </div>
    </section>

    <!-- Блок 2: С какими объектами работаем -->
    <section class="p42-objects">
      <div class="container">
        <h2 class="title center p42-objects__heading">С какими объектами работаем</h2>
      </div>
      <div class="p42-objects__slider">
        <ClientOnly>
          <SliderSwiperNuxt :swiperSlides="objectSlides" :swiperSetting="objectsSliderSettings">
            <template #sliderBody="{ slide }">
              <div class="p42-objects__card">
                <div class="p42-objects__card-img">
                  <img :src="slide.img" :alt="slide.name" loading="lazy" />
                </div>
                <div class="p42-objects__card-name">{{ slide.name }}</div>
                <div class="p42-objects__card-desc">{{ slide.desc }}</div>
              </div>
            </template>
          </SliderSwiperNuxt>
        </ClientOnly>
      </div>
    </section>

    <!-- Блок 3: Перепланировка под ключ -->
    <section class="p42-steps" id="replan">
      <div class="container">
        <h2 class="title center p42-steps__heading">Перепланировка под ключ</h2>
        <p class="p42-steps__lead">Согласуем, спроектируем, реализуем.</p>
        <div class="p42-steps__track">
          <template v-for="(step, i) in replanSteps" :key="step.title">
            <div class="p42-steps__step">
              <div class="p42-steps__num">{{ i + 1 }}</div>
              <div class="p42-steps__step-title">{{ step.title }}</div>
              <div class="p42-steps__step-text">{{ step.text }}</div>
            </div>
            <div v-if="i < replanSteps.length - 1" class="p42-steps__connector" aria-hidden="true">
              <svg class="p42-steps__arrow-h" width="56" height="16" viewBox="0 0 56 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="8" x2="46" y2="8" stroke="#D0D0D0" stroke-width="1.5"/>
                <polyline points="38,2 50,8 38,14" fill="none" stroke="#D0D0D0" stroke-width="1.5" stroke-linejoin="round"/>
              </svg>
              <svg class="p42-steps__arrow-v" width="16" height="56" viewBox="0 0 16 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="8" y1="0" x2="8" y2="46" stroke="#D0D0D0" stroke-width="1.5"/>
                <polyline points="2,38 8,50 14,38" fill="none" stroke="#D0D0D0" stroke-width="1.5" stroke-linejoin="round"/>
              </svg>
            </div>
          </template>
        </div>
        <div class="p42-steps__image">
          <img src="/img/services/arch/replanning.jpg" alt="Перепланировка под ключ" loading="lazy" />
        </div>
      </div>
    </section>

    <!-- Блок 4: Атмосферная галерея -->
    <section class="p42-gallery">
      <div class="container">
        <h2 class="title center p42-gallery__heading">Мы создаём не здания, а атмосферу</h2>
        <p class="p42-gallery__lead">Каждое наше решение — это среда обитания, которая наполняет людей энергией жизни</p>
      </div>
      <div class="container p42-gallery__body">
        <ClientOnly>
          <MasonryWall :items="galleryItems" :column-width="360" :gap="16" :max-columns="3">
            <template #default="{ item, index }">
              <div class="p42-gallery__item" @click="openLightbox(index)">
                <img :src="item.src" :alt="'Архитектура — работа ' + (index + 1)" loading="lazy" />
              </div>
            </template>
          </MasonryWall>
        </ClientOnly>
      </div>
    </section>

    <!-- Блок 5: Форма заявки -->
    <section class="p42-form" id="request-form">
      <div class="container">
        <div class="p42-form__layout">

          <div class="p42-form__aside">
            <div class="p42-form__aside-label">Запрос</div>
            <h2 class="title p42-form__heading">Обсудить проект</h2>
            <p class="p42-form__aside-text">
              Расскажите о задаче — архитектор свяжется с вами для первичной консультации.
            </p>
            <ul class="p42-form__aside-facts">
              <li>Консультация в день обращения</li>
              <li>Предварительная оценка стоимости</li>
              <li>Конфиденциальность проекта</li>
            </ul>
          </div>

          <div class="p42-form__body">
            <div v-if="reqSuccess" class="p42-form__success-screen">
              <div class="p42-form__success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="23" stroke="#1B1918" stroke-width="1.5"/>
                  <polyline points="14,24 21,31 34,17" fill="none" stroke="#1B1918" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="p42-form__success-title">Запрос принят</h3>
              <p class="p42-form__success-desc">Архитектор свяжется с вами в ближайшее время для первичной консультации.</p>
              <button class="p42-form__success-reset" @click="reqSuccess = false">Отправить ещё один запрос</button>
            </div>
            <form v-else @submit.prevent="submitRequest" novalidate>
              <div class="p42-form__row">
                <div class="p42-form__field">
                  <Input placeholder="Как вас зовут" v-model="reqName" class="p42-fi" />
                </div>
                <div class="p42-form__field">
                  <Input placeholder="+7 000 000-00-00" v-model="reqPhone" type="tel" class="p42-fi" />
                </div>
              </div>
              <div class="p42-form__field">
                <Input placeholder="Ваш email (необязательно)" v-model="reqEmail" type="email" class="p42-fi" />
              </div>
              <div class="p42-form__field">
                <Textarea
                  placeholder="Тип объекта, площадь, стиль, особые пожелания"
                  v-model="reqComment"
                  class="p42-ft"
                />
              </div>
              <div
                class="p42-form__drop"
                :class="{ 'is-over': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onFormDrop"
                @click="formFileRef?.click()"
              >
                <input
                  ref="formFileRef"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.dwg"
                  class="p42-form__file-input"
                  @change="onFormFilePick"
                />
                <template v-if="!reqFiles.length">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span class="p42-form__drop-main">Прикрепите эскиз, ТЗ или референсы</span>
                  <span class="p42-form__drop-sub">Перетащите файлы или нажмите · JPG, PNG, PDF, DWG · до 20 МБ</span>
                </template>
                <div v-else class="p42-form__drop-files">
                  <span v-for="f in reqFiles" :key="f.name" class="p42-form__drop-file">{{ f.name }}</span>
                </div>
              </div>
              <div class="p42-form__checks">
                <label class="p42-form__check">
                  <input type="checkbox" class="p42-form__check-input" @change="reqAgreedPrivacy = $event.target.checked" />
                  <span class="p42-form__check-box"></span>
                  <span class="p42-form__check-text">Я согласен с <a href="/privacy" target="_blank">обработкой персональных данных</a></span>
                </label>
                <label class="p42-form__check">
                  <input type="checkbox" class="p42-form__check-input" @change="reqAgreedOferta = $event.target.checked" />
                  <span class="p42-form__check-box"></span>
                  <span class="p42-form__check-text">Я согласен с <a href="/oferta" target="_blank">публичной офертой</a></span>
                </label>
              </div>
              <button type="submit" class="p42-form__btn" :disabled="!reqAgreedPrivacy || !reqAgreedOferta">Отправить запрос</button>
              <p v-if="reqError" class="p42-form__error">{{ reqError }}</p>
            </form>
          </div>

        </div>
      </div>
    </section>

    <!-- Блок 5: FAQ -->
    <section class="p42-faq">
      <div class="container">
        <h2 class="title center p42-faq__heading">Часто задаваемые вопросы</h2>
        <div class="p42-faq__list">
          <Spoiler
            v-for="(item, i) in faqItems"
            :key="item.q"
            :item="{ label: item.q, content: item.a }"
            :isActive="faqActive === i"
            @toggleSpoiler="faqActive = faqActive === i ? null : i"
          />
        </div>
      </div>
    </section>

  </main>

  <ClientOnly>
    <teleport to="body">
      <FormArchitecture v-if="isModalOpen" @close="isModalOpen = false" />
      <Lightbox v-if="lbOpen" :items="galleryItems" :start-index="lbIndex" @close="lbOpen = false" />
      <Cookie />
    </teleport>
  </ClientOnly>
</template>

<script setup>
import Breadcrumbs from '~/components/Section/Breadcrumbs.vue'
import FormArchitecture from '~/components/Modals/FormArchitecture.vue'
import SliderSwiperNuxt from '~/components/UI/Slider/SliderSwiperNuxt.vue'
import Spoiler from '~/components/UI/Containers/Spoiler.vue'
import Lightbox from '~/components/Modals/Lightbox.vue'
import Input from '~/components/UI/inputs/Input.vue'
import Textarea from '~/components/UI/inputs/Textarea.vue'
import Cookie from '~/components/Section/Cookie.vue'
import api from '~/api/api.js'

const isModalOpen = ref(false)

const { data: archGalleryPaths } = await useFetch('/api/gallery/arch')
const galleryItems = computed(() =>
  (archGalleryPaths.value ?? []).map(src => ({ src }))
)

const lbOpen = ref(false)
const lbIndex = ref(0)

function openLightbox(i) {
  lbIndex.value = i
  lbOpen.value = true
}

const reqName    = ref('')
const reqPhone   = ref('')
const reqEmail   = ref('')
const reqComment = ref('')
const reqFiles   = ref([])
const reqAgreedPrivacy = ref(false)
const reqAgreedOferta  = ref(false)
const reqSuccess = ref(false)
const reqError   = ref('')
const isDragOver = ref(false)
const formFileRef = ref(null)

watch(reqPhone, (val) => {
  let digits = val.replace(/\D/g, '')
  if (digits.startsWith('7') || digits.startsWith('8')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  let masked = '+7'
  if (digits.length > 0) masked += ' ' + digits.slice(0, 3)
  if (digits.length > 3) masked += ' ' + digits.slice(3, 6)
  if (digits.length > 6) masked += '-' + digits.slice(6, 8)
  if (digits.length > 8) masked += '-' + digits.slice(8, 10)
  if (reqPhone.value !== masked) reqPhone.value = masked
})

function onFormFilePick(e) {
  reqFiles.value = Array.from(e.target.files)
}

function onFormDrop(e) {
  isDragOver.value = false
  reqFiles.value = Array.from(e.dataTransfer.files)
}

async function submitRequest() {
  reqError.value = ''
  if (!reqName.value.trim() || !reqPhone.value.trim()) {
    reqError.value = 'Укажите имя и телефон.'
    return
  }
  if (!reqAgreedPrivacy.value || !reqAgreedOferta.value) {
    reqError.value = 'Подтвердите оба согласия.'
    return
  }
  const fd = new FormData()
  fd.append('product_id', '42')
  fd.append('name', reqName.value)
  fd.append('phone', reqPhone.value)
  if (reqEmail.value) fd.append('email', reqEmail.value)
  if (reqComment.value) fd.append('comment', reqComment.value)
  reqFiles.value.forEach(f => fd.append('files[]', f))
  try {
    await api('/order', fd, 'POST')
    reqSuccess.value = true
    reqName.value = ''
    reqPhone.value = ''
    reqEmail.value = ''
    reqComment.value = ''
    reqFiles.value = []
  } catch {
    reqError.value = 'Произошла ошибка. Попробуйте ещё раз или позвоните нам.'
  }
}

useHead({
  title: 'Архитектура — от концепции до сдачи объекта | Шарунстор',
  meta: [
    {
      name: 'description',
      content: 'Проектирование с нуля, визуализация, авторский надзор и сдача под ключ. Жилые дома, отели, офисы, торговые пространства и рестораны. Проверенная команда архитекторов через платформу Sharun.'
    }
  ]
})

const objectSlides = [
  {
    name: 'Жилые дома',
    desc: 'Частные дома и коттеджи. Проектируем под образ жизни — от генплана участка до отделки.',
    img: '/img/services/arch/home.jpg',
  },
  {
    name: 'Отели',
    desc: 'Бутик-отели, загородные комплексы, апарт-отели. Архитектура, которая работает на впечатление гостя.',
    img: '/img/services/arch/hotel.jpg',
  },
  {
    name: 'Офисные центры',
    desc: 'Бизнес-центры и корпоративные штаб-квартиры. Среда, в которой хочется работать.',
    img: '/img/services/arch/office.jpg',
  },
  {
    name: 'Торговые пространства',
    desc: 'Торговые центры, шоурумы, концептуальные магазины. Архитектура как инструмент продаж.',
    img: '/img/services/arch/trade.jpg',
  },
  {
    name: 'Кафе и рестораны',
    desc: 'Концепция, атмосфера, поток гостей — всё закладывается на этапе проекта.',
    img: '/img/services/arch/horeca.jpg',
  },
]

const objectsSliderSettings = {
  spaceBetween: 24,
  loop: false,
  breakpoints: {
    0:    { slidesPerView: 1.3, spaceBetween: 16 },
    768:  { slidesPerView: 2.5, spaceBetween: 20 },
    1200: { slidesPerView: 3.5, spaceBetween: 24 },
  },
}

const replanSteps = [
  {
    title: 'Проект перепланировки',
    text: 'Обмерный план, проектная документация, визуализация нового решения.',
  },
  {
    title: 'Согласование',
    text: 'Берём на себя взаимодействие с надзорными органами. Вы не тратите время на бюрократию.',
  },
  {
    title: 'Реализация',
    text: 'Партнёры платформы выполняют работы по согласованному проекту.',
  },
]

const faqActive = ref(null)
const faqItems = [
  {
    q: 'С чего начинается работа?',
    a: 'С первичной консультации. Архитектор выслушает задачу, предложит подход и озвучит стоимость разработки концепции.',
  },
  {
    q: 'Сколько стоит проект?',
    a: 'Зависит от типа объекта, площади и состава работ. Стоимость определяется после первичного брифинга.',
  },
  {
    q: 'Вы работаете только в Москве?',
    a: 'Проектируем для объектов по всей России. Авторский надзор — по договорённости.',
  },
  {
    q: 'Можно заказать только концепцию и визуализацию?',
    a: 'Да, это отдельная услуга. Укажите в примечании, что вам нужно.',
  },
  {
    q: 'Что входит в сдачу под ключ?',
    a: 'Проект, согласования, авторский надзор и контроль строительства. Итог — готовый объект без вашего погружения в процесс.',
  },
  {
    q: 'Занимаетесь дизайном интерьера и ремонтом?',
    a: 'Да, это отдельная услуга платформы. Работаем с проверенными дизайнерами и бригадами — от концепции до финишной отделки. Подробнее об услуге →',
  },
  {
    q: 'Можете помочь с ландшафтным дизайном участка?',
    a: 'Да, у нас есть партнёр по ландшафтному дизайну. Если проектируете дом — имеет смысл решать архитектуру и участок в одной концепции. Укажите в заявке, и мы передадим запрос нужному специалисту.',
  },
]

const breadcrumbs = [
  { title: 'Главная', url: '/' },
  { title: 'Каталог', url: '/catalog' },
  { title: 'Архитектура' }
]
</script>

<style scoped lang="scss">
$mobile: 48em;
$tablet: 75em;
$dark: #1B1918;
$grey: #666;

.p42-hero {
  padding: 6rem 0 8rem;

  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 4rem;
    }
  }

  &__title {
    font-size: 5rem;
    line-height: 1.12;
    margin-bottom: 2.4rem;
    color: $dark;

    @media (max-width: $tablet) {
      font-size: 4rem;
    }

    @media (max-width: $mobile) {
      font-size: 3.2rem;
    }
  }

  &__desc {
    font-size: 1.8rem;
    line-height: 1.6;
    color: $grey;
    margin-bottom: 3.2rem;
    max-width: 52rem;
  }

  &__geo {
    margin-top: 1.6rem;
    font-size: 1.4rem;
    color: #aaa;
  }

  &__image {
    img {
      width: 100%;
      height: 56rem;
      object-fit: cover;
      border-radius: 0.4rem;
      display: block;

      @media (max-width: $tablet) {
        height: 40rem;
      }

      @media (max-width: $mobile) {
        height: 28rem;
      }
    }
  }
}
// ─── Блок 2: С какими объектами работаем ─────────────────────────────────────
.p42-objects {
  padding: 8rem 0;

  &__heading {
    margin-bottom: 4rem;
  }

  &__slider {
    padding-left: 3.2rem;

    @media (max-width: $mobile) {
      padding-left: 2rem;
    }
  }

  &__card {
    padding-right: 0.4rem;
  }

  &__card-img {
    img {
      width: 100%;
      aspect-ratio: 4 / 5;
      object-fit: cover;
      border-radius: 5px;
      display: block;
    }
  }

  &__card-name {
    padding: 1.5rem 0 0.6rem;
    font-size: 1.6rem;
    font-weight: 700;
    color: $dark;
    text-align: center;
  }

  &__card-desc {
    font-size: 1.4rem;
    color: $grey;
    line-height: 1.5;
    text-align: center;
  }
}
// ─── Блок 3: Перепланировка под ключ ─────────────────────────────────────────
.p42-steps {
  padding: 8rem 0;
  background: #f7f7f7;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    margin: 0 auto 5.6rem;
    line-height: 1.6;
  }

  &__track {
    display: flex;
    align-items: flex-start;
    justify-content: center;

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: center;
    }
  }

  &__step {
    flex: 1;
    max-width: 28rem;
    text-align: center;

    @media (max-width: $mobile) {
      max-width: 32rem;
      width: 100%;
    }
  }

  &__num {
    font-size: 7.2rem;
    font-weight: 800;
    line-height: 1;
    color: rgba(27, 25, 24, 0.08);
    margin-bottom: 1.6rem;
    font-variant-numeric: tabular-nums;
  }

  &__step-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $dark;
    margin-bottom: 1.2rem;
    line-height: 1.3;
  }

  &__step-text {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.6;
  }

  &__connector {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 2.4rem;
    margin-top: 3.4rem;

    @media (max-width: $mobile) {
      padding: 2rem 0;
      margin-top: 0;
    }
  }

  &__arrow-h {
    display: block;

    @media (max-width: $mobile) {
      display: none;
    }
  }

  &__arrow-v {
    display: none;

    @media (max-width: $mobile) {
      display: block;
    }
  }

  &__image {
    margin-top: 6rem;

    img {
      width: 100%;
      display: block;
      border-radius: 8px;
      object-fit: cover;
    }
  }
}

// ─── Блок 4: Форма заявки ────────────────────────────────────────────────────
.p42-form {
  padding: 10rem 0;
  background: $dark;

  &__layout {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 8rem;
    align-items: start;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 5rem;
    }
  }

  &__aside {
    padding-top: 1.6rem;
    color: #fff;
  }

  &__aside-label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 2.4rem;
  }

  &__heading {
    color: #fff;
    margin-bottom: 2.4rem;
    font-size: 4.8rem;

    @media (max-width: $tablet) {
      font-size: 4rem;
    }
  }

  &__aside-text {
    font-size: 1.6rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 3.6rem;
  }

  &__aside-facts {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;

    li {
      font-size: 1.5rem;
      color: rgba(255, 255, 255, 0.7);
      padding-left: 2rem;
      position: relative;

      &::before {
        content: '—';
        position: absolute;
        left: 0;
        color: rgba(255, 255, 255, 0.3);
      }
    }
  }

  &__body {
    background: #fff;
    border-radius: 12px;
    padding: 4.8rem;

    @media (max-width: $mobile) {
      padding: 3.2rem 2.4rem;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
  }

  &__drop {
    border: 1.5px dashed #D8D8D8;
    border-radius: 6px;
    padding: 2.8rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    color: #B0B0B0;

    svg { color: #CCC; margin-bottom: 0.4rem; }

    &.is-over { border-color: $dark; background: #f5f5f5; }
    &:hover { border-color: #aaa; }
  }

  &__file-input { display: none; }

  &__drop-main {
    font-size: 1.5rem;
    color: $dark;
    font-weight: 500;
  }

  &__drop-sub {
    font-size: 1.3rem;
    color: #B0B0B0;
  }

  &__drop-files {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    justify-content: center;
  }

  &__drop-file {
    font-size: 1.3rem;
    background: #F0F0F0;
    border-radius: 4px;
    padding: 0.4rem 1rem;
    color: $dark;
  }

  &__checks {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  &__check {
    display: flex;
    align-items: flex-start;
    gap: 1.2rem;
    cursor: pointer;
  }

  &__check-input {
    display: none;

    &:checked + .p42-form__check-box {
      background: $dark;
      border-color: $dark;

      &::after { opacity: 1; }
    }
  }

  &__check-box {
    flex-shrink: 0;
    width: 1.8rem;
    height: 1.8rem;
    border: 1.5px solid #C0C0C0;
    border-radius: 3px;
    margin-top: 0.2rem;
    position: relative;
    transition: background 0.15s, border-color 0.15s;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpolyline points='1,5 4.5,8.5 11,1' fill='none' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 1.1rem no-repeat;
      opacity: 0;
      transition: opacity 0.15s;
    }
  }

  &__check-text {
    font-size: 1.4rem;
    color: $grey;
    line-height: 1.5;

    a { color: $dark; text-decoration: underline; }
  }

  &__btn {
    width: 100%;
    height: 5.6rem;
    background: $dark;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 1.5rem;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: background 0.2s;

    &:hover { background: #333; }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
  }

  &__success-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
    min-height: 36rem;
    gap: 2rem;
  }

  &__success-icon {
    color: $dark;
    line-height: 0;
  }

  &__success-title {
    font-size: 2.8rem;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__success-desc {
    font-size: 1.6rem;
    color: $grey;
    line-height: 1.6;
    max-width: 38rem;
  }

  &__success-reset {
    margin-top: 1.2rem;
    background: none;
    border: none;
    font-size: 1.4rem;
    color: #aaa;
    cursor: pointer;
    font-family: 'Manrope', sans-serif;
    text-decoration: underline;
    text-underline-offset: 3px;
    padding: 0;
    transition: color 0.15s;

    &:hover { color: $dark; }
  }

  &__error {
    font-size: 1.4rem;
    color: #eb2a0d;
    text-align: center;
  }
}

// ─── Блок 4: Атмосферная галерея ─────────────────────────────────────────────
.p42-gallery {
  padding: 8rem 0;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    max-width: 64rem;
    margin: 0 auto 5.6rem;
    line-height: 1.6;
  }

  &__body {
    max-width: 70%;
    margin: 0 auto;

    @media (max-width: $tablet) {
      max-width: 100%;
    }
  }

  &__item {
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.88;
    }

    img {
      width: 100%;
      display: block;
    }
  }
}

// ─── Блок 5: FAQ ─────────────────────────────────────────────────────────────
.p42-faq {
  padding: 8rem 0;
  background: #f7f7f7;

  &__heading {
    margin-bottom: 5.6rem;
  }

  &__list {
    max-width: 80rem;
    margin: 0 auto;
  }
}

.p42-fi {
  width: 100% !important;
  height: 5.2rem !important;
  padding: 0 2rem !important;
  font-size: 1.5rem !important;
  font-family: 'Manrope', sans-serif !important;
  color: $dark !important;
  background: #FAFAFA !important;
  border: 1.5px solid #D8D8D8 !important;
  border-radius: 6px !important;
  transition: border-color 0.2s, background 0.2s !important;

  &:focus {
    border-color: $dark !important;
    background: #fff !important;
    outline: none !important;
  }

  &::placeholder { color: #B0B0B0 !important; }
}

.p42-ft {
  width: 100% !important;
  height: 12rem !important;
  padding: 1.6rem 2rem !important;
  font-size: 1.5rem !important;
  font-family: 'Manrope', sans-serif !important;
  color: $dark !important;
  background: #FAFAFA !important;
  border: 1.5px solid #D8D8D8 !important;
  border-radius: 6px !important;
  resize: none !important;
  line-height: 1.6 !important;
  transition: border-color 0.2s, background 0.2s !important;

  &:focus {
    border-color: $dark !important;
    background: #fff !important;
    outline: none !important;
  }

  &::placeholder { color: #B0B0B0 !important; }
}
</style>
